import { Button, cn } from '@seen/ui'
import { IconChevronDown, IconX } from '@tabler/icons-react'
import { cva, type VariantProps } from 'class-variance-authority'
import { type JSX, type ReactNode, useCallback, useMemo, useState } from 'react'
import { Checkbox } from '#src/components/core/Checkbox.tsx'
import {
  type ComboboxOption,
  MAX_OPTIONS_TO_SHOW,
  useComboboxFiltering,
} from '#src/components/core/combobox/util.ts'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '#src/components/core/Command.tsx'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'

export type FilterChipOption<V extends string = string> = ComboboxOption<V>

export interface FilterChipProps<V extends string = string> {
  label: string
  options: FilterChipOption<V>[]
  values: V[]
  onChange: (values: V[]) => void
  icon?: ReactNode
  disableSearch?: boolean
  className?: string
  searchPlaceholder?: string
}

const filterChipVariants = cva(
  'inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 font-medium text-sm transition-colors',
  {
    variants: {
      active: {
        false:
          'border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800/50',
        true: 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300',
      },
    },
    defaultVariants: {
      active: false,
    },
  }
)

export type FilterChipVariantProps = VariantProps<typeof filterChipVariants>

export function FilterChip<V extends string = string>({
  label,
  options,
  values,
  onChange,
  icon,
  disableSearch = false,
  className,
  searchPlaceholder = 'Search...',
}: FilterChipProps<V>): JSX.Element {
  const [open, setOpen] = useState(false)

  const isActive = values.length > 0

  const displayLabel = useMemo(() => {
    if (values.length === 0) {
      return label
    }
    if (values.length === 1) {
      const selectedOption = options.find((opt) => opt.value === values[0])
      return selectedOption?.label ?? values[0]
    }
    return `${values.length} selected`
  }, [values, options, label])

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      onChange([])
    },
    [onChange]
  )

  const handleSelect = useCallback(
    (value: V) => {
      const isSelected = values.includes(value)
      if (isSelected) {
        onChange(values.filter((v) => v !== value))
      } else {
        onChange([...values, value])
      }
    },
    [values, onChange]
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="unstyled"
          size="unstyled"
          className={cn(filterChipVariants({ active: isActive }), className)}
        >
          {icon && <span className="shrink-0 opacity-70">{icon}</span>}
          <span className="max-w-[150px] truncate">{displayLabel}</span>
          {isActive && (
            <button
              type="button"
              onClick={handleClear}
              className="-mr-0.5 rounded p-0.5 hover:bg-blue-200/50 dark:hover:bg-blue-800/50"
            >
              <IconX className="size-3.5" />
            </button>
          )}
          <IconChevronDown
            className={cn(
              'size-3.5 shrink-0 opacity-50 transition-transform',
              open && 'rotate-180'
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <FilterChipCommand
          options={options}
          values={values}
          onSelect={handleSelect}
          disableSearch={disableSearch}
          searchPlaceholder={searchPlaceholder}
        />
      </PopoverContent>
    </Popover>
  )
}

interface FilterChipCommandProps<V extends string = string> {
  options: FilterChipOption<V>[]
  values: V[]
  onSelect: (value: V) => void
  disableSearch?: boolean
  searchPlaceholder?: string
}

function FilterChipCommand<V extends string = string>({
  options,
  values,
  onSelect,
  disableSearch,
  searchPlaceholder,
}: FilterChipCommandProps<V>): JSX.Element {
  const [search, setSearch] = useState('')

  const { filteredOptions } = useComboboxFiltering({
    options,
    suggestedOptions: [],
    search,
  })

  const displayOptions = search ? filteredOptions : options

  return (
    <Command shouldFilter={false}>
      {!disableSearch && (
        <CommandInput
          onValueChange={setSearch}
          placeholder={searchPlaceholder}
          className="rounded-none border-b px-3"
          autoFocus
        />
      )}
      <CommandList className="max-h-[300px]">
        <CommandEmpty>No options found.</CommandEmpty>
        {displayOptions.slice(0, MAX_OPTIONS_TO_SHOW).map((option) => {
          const isSelected = values.includes(option.value)
          return (
            <CommandItem
              key={option.value}
              value={option.value}
              onSelect={() => onSelect(option.value)}
              className="group flex cursor-pointer items-center justify-between gap-2"
            >
              <Checkbox
                className={cn(
                  'shrink-0',
                  !isSelected && 'opacity-0 group-hover:opacity-50'
                )}
                value={isSelected}
              />
              <span className="flex-1 truncate">{option.label}</span>
            </CommandItem>
          )
        })}
        {displayOptions.length > MAX_OPTIONS_TO_SHOW && (
          <CommandItem disabled>Search to see more results...</CommandItem>
        )}
      </CommandList>
    </Command>
  )
}
